import React, { useState } from 'react';
import { Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

interface DocumentViewerSectionProps {
  pdfUrl: string;
  hasViewedLOA: boolean;
  setHasViewedLOA: (viewed: boolean) => void;
  sectionStyle: any;
  token?: string;
  clientId?: string;
  onUrlRefresh?: (newUrl: string) => void;
}

const DocumentViewerSection: React.FC<DocumentViewerSectionProps> = ({
  pdfUrl,
  hasViewedLOA,
  setHasViewedLOA,
  sectionStyle,
  token,
  clientId,
  onUrlRefresh
}) => {
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [freshPdfUrl, setFreshPdfUrl] = useState<string>('');
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  const handleOpenPdf = async () => {
    // Always fetch a fresh presigned URL before opening the modal
    if (clientId) {
      try {
        setLoadingUrl(true);
        setUrlError(null);
        console.log('Fetching fresh presigned URL for viewing...');
        
        const response = await axios.get(`/api/loa/public/get-presigned-url/${clientId}`);
        
        if (response.data.success && response.data.url) {
          console.log('Fresh URL obtained for viewing:', response.data.url);
          const newUrl = response.data.url;
          setFreshPdfUrl(newUrl);
          
          // Notify parent component of the new URL
          if (onUrlRefresh) {
            onUrlRefresh(newUrl);
          }
          
          // Only open modal after we have the fresh URL
          setShowPdfModal(true);
          setHasViewedLOA(true);
        } else {
          setUrlError('Failed to load document URL');
        }
      } catch (error) {
        console.error('Error fetching fresh PDF URL:', error);
        setUrlError('Failed to load document. Please try again.');
      } finally {
        setLoadingUrl(false);
      }
    } else {
      console.warn('No clientId provided, using fallback URL');
      // Fallback to existing URL if no clientId provided
      setFreshPdfUrl(pdfUrl);
      setShowPdfModal(true);
      setHasViewedLOA(true);
    }
  };

  const handleClosePdf = () => {
    setShowPdfModal(false);
  };

  return (
    <>
      <Box mb={4} sx={sectionStyle}>
        <Typography variant="h6" gutterBottom color="primary">
          Letter of Authority Document
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={2}>
          Please review the Letter of Authority document before accepting or declining:
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={loadingUrl ? <CircularProgress size={20} color="inherit" /> : <VisibilityIcon />}
            onClick={handleOpenPdf}
            size="large"
            disabled={loadingUrl}
          >
            {loadingUrl ? 'Loading Document...' : 'View LOA Document'}
          </Button>
        </Box>
        {urlError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {urlError}
          </Alert>
        )}
        {hasViewedLOA && !urlError && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Document viewed successfully. You may now accept or decline below.
          </Alert>
        )}
      </Box>

      {/* PDF Viewer Modal - Full Screen */}
      <Dialog
        open={showPdfModal}
        onClose={handleClosePdf}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: '#525659'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            backgroundColor: '#2c3e50',
            color: 'white',
            py: 2
          }}
        >
          <Typography variant="h6">Letter of Authority</Typography>
          <IconButton onClick={handleClosePdf} edge="end" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(100vh - 120px)', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#525659' }}>
          <Box sx={{ width: '100%', maxWidth: '1200px', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <iframe
              src={`${freshPdfUrl || pdfUrl}#zoom=FitH&view=FitH`}
              width="100%"
              height="100%"
              style={{
                border: 'none',
                backgroundColor: 'white'
              }}
              title="Letter of Authority"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#2c3e50', py: 2 }}>
          <Button onClick={handleClosePdf} variant="contained" color="primary" size="large">
            Close and Return to Form
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentViewerSection;
